import { makeStyles } from '@material-ui/styles';
import { CircularProgress, Container } from '@material-ui/core';
import AccountMenu from '@components/account/AccountMenu';
import If from '@components/misc/If';
import { useTranslation } from '@tools/i18n';
import useUserStore from '@services/stores/UserStore';
import { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
// @ts-ignore
import theme from '@components/theme';
import SimpleAccordion from '@components/accordion/SimpleAccordion';
import CallToActionButton from '@components/products/partials/CallToActionButton';
import useApplicationStore from '@services/stores/ApplicationStore';
import AccountLogin from '@components/account/AccountLogin';
import ElevateUpsell from '@components/header/cart/elevateUpsell';

const useStyles = makeStyles({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        margin: '0 0 40px',
        gap: 20,
        // @ts-ignore
        [theme.breakpoints.up('md')]: {
            //display: 'grid',
            //gridTemplateColumns: '300px 1fr',
            flexDirection: 'row',
            margin: '40px 0',
        },
    },
    aside: {
        width: '100%',
        // @ts-ignore
        [theme.breakpoints.up('md')]: {
            maxWidth: '350px',
        }
    },
    menu: {
        background: '#f8f8f8',
    },
    logo: {
        maxWidth: 150,
    },
    header: {
        background: theme.palette.primary.dark,
        padding: 10,
    },
    headerBar: {
        '&.notLoggedIn':{
            justifyContent: 'center',
        },
        // @ts-ignore
        [theme.breakpoints?.up('md')]: {
            display: 'grid',
            gridTemplateColumns: 'max-content min-content',
        },
        justifyContent: 'space-between',
        marginBottom: 20,
        '& > h1': {
            marginBottom: 10,
        },
    },
    title: {
        fontFamily: theme.typography.fontFamily,
        marginTop: 0,
    },
    content: {
        width: '100%',
    },
    myPageContent: {
        maxWidth: '75ch',
    },
    pageError: {},
    loginRequired: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        maxWidth: 400,
        margin: 'auto',
    },
    tools: {
        display: 'flex',
        gap: 5,
        alignItems: 'flex-end',
        // @ts-ignore
        [theme.breakpoints?.up('md')]: {
            paddingBottom: 10,
            gap: 10,
        },
    },
}, { name: 'navigation-layout' });

interface NavigationLayoutInterface {
    title: string;
    tools?: JSX.Element;
    loading?: boolean;
    error?: boolean;
}

export default function NavigationLayout(
    {
        children,
        title,
        tools,
        loading,
        error,
    }: PropsWithChildren<NavigationLayoutInterface>): ReactElement {
    const classes = useStyles();
    const { t } = useTranslation(['account']);
    const isLoggedIn = useUserStore((state) => state.isLoggedIn);
    const appMethods = useApplicationStore((state) => state.methods);
    const isMobile = useApplicationStore((state) => state.isMobile);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true)
    }, []);

    function renderPageError() {
        if (error == undefined || !error) {
            return null;
        }

        if (!isLoggedIn) {
            return null;
        }

        return <section className={classes.pageError}>
            {t('account:unexpected-page-error')}
        </section>;
    }

    function renderChildren(children: any) {
        if (!isLoggedIn) {
            return <div className={classes.loginRequired}>
               <AccountLogin />
            </div>;
        }

        if (isLoggedIn && !loading) {
           if (children) { return children;}
           else {
               return (
                   <>
                       <div className={classes.myPageContent} dangerouslySetInnerHTML={{ __html: t('account:account-my-page-content') }}></div>
                   </>
               )
           }
        }
    }

    function renderMenu() {
        if (isMobile) {
            return <div>
                <SimpleAccordion defaultExpanded={false} title={t('account:label-menu')}>
                    <AccountMenu />
                </SimpleAccordion>
            </div>;
        }

        return <SimpleAccordion defaultExpanded={true} title={t('account:label-menu')}>
            <AccountMenu />
        </SimpleAccordion>;
    }

    return <Container>
        <div className={classes.wrapper}>
            {isLoggedIn &&
                <aside className={classes.aside}>
                    { renderMenu() }
                </aside>
            }
            <main className={classes.content}>
                <header className={[classes.headerBar, !isLoggedIn ? 'notLoggedIn' : ''].join(' ')}>
                    <If condition={title}>
                        <h1 className={classes.title}>
                            {title}
                            <If condition={loading}>
                                <CircularProgress style={{ marginLeft: 10 }} size={24}></CircularProgress>
                            </If>
                        </h1>
                    </If>
                    <If condition={tools}>
                        <div className={classes.tools}>
                            {tools}
                        </div>
                    </If>
                </header>
                <div>
                    {renderPageError()}
                    {renderChildren(children)}
                </div>
            </main>
        </div>
    </Container>;
}
